
import { Component, Emit, InjectReactive, Vue } from 'vue-property-decorator';
import { Action, Getter, Mutation } from 'vuex-class';

import CategorySelector from '@/components/CategorySelector.vue';
import ContinueShoppingModal from '@/components/ContinueShoppingModal.vue';
import { MutationTypes, Views } from '@/enums';
import BasketModule from '@/store/modules/basket';

const { SET_ACTIVE_CATEGORY, SET_ACTIVE_PRODUCT } = MutationTypes;

@Component({
  components: {
    CategorySelector,
  },
  metaInfo: {
    title: 'Cousins Subs',
    titleTemplate: '%s - Order - Choose Food'
  }
})
export default class ChooseFood extends Vue {
  @Getter categories!: Category[];
  @Getter activeCategory!: Category;
  @Getter activeRestaurant: Restaurant;
  @Getter editIsOpen: boolean;
  @Action fetchRestaurantMenu: ({ slug }: { slug: string }) => void;
  @Action fetchRestaurantBySlug: ({ slug }: { slug: string }) => Restaurant;;
  @Action fetchRestaurant: ({ restaurantId }: { restaurantId: number }) => Restaurant;
  @Mutation(SET_ACTIVE_CATEGORY) setActiveCategory: (category: Category | null) => void;
  @Mutation(SET_ACTIVE_PRODUCT) setActiveProduct: (product: Product) => void;
  @InjectReactive() isMobile!: boolean;
  @InjectReactive('progressRef') progressRef: any;

  async created() {
    const { location, categorySlug } = this.$route.params;
    let restaurant: Restaurant = this.activeRestaurant;

    if (location && (restaurant?.slug !== location || (BasketModule.current.id && BasketModule.data.vendorId !== restaurant.id))) {
      if (!BasketModule.current?.id) {
        restaurant = await this.fetchRestaurantBySlug({ slug: location });
      } else {
        restaurant = await this.fetchRestaurant({ restaurantId: BasketModule.data.vendorId });
      }

      if (restaurant.slug !== location) {
        this.$router.push({
          name: this.$route.name as string,
          params: {
            ...this.$route.params,
            location: restaurant.slug,
          }
        });
      }
    }

    if (restaurant?.slug) {
      await this.fetchRestaurantMenu({ slug: restaurant.slug });
    }

    if (categorySlug) {
      const category = this.getCategoryFromSlug();
      if (category.id) {
        this.setActiveCategory(category);
      } else {
        this.$router.push({
          name: 'ChooseFood',
          params: {
            ...this.$route.params,
            categorySlug: '',
          }
        });
      }
    }
  }

  mounted() {
    this.$scrollTo(this.progressRef as Element);
  }

  getCategoryFromSlug(): Category {
    const { categorySlug } = this.$route.params;
    return this.categories.find((category) => category.slug === categorySlug) as Category || {} as Category;
  }

  @Emit('handleChooseCategory')
  handleChooseCategory(category) {
    if (this.isMobile) {
      this.$scrollTo(this.$refs.products_container as Element, { force: true });
    } else {
      this.$scrollTo(this.$refs.category_container as Element, { force: true });
    }
    return { category, callback: undefined };
  }

  hideProducts() {
    this.setActiveCategory(null);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { category, ...rest } = this.$route.params;
    this.$router.push({
      name: 'ChooseFood',
      params: {
        ...rest,
      },
    });
  }

  handleSingleProductError() {
    //
  }

  handleSingleProductSuccess(event) {
    this.$modal.show(
      ContinueShoppingModal, {
        isMobile: this.isMobile,
        message: event?.success,
      }, {
        clickToClose: false,
        scrollable: true,
        focusTrap: true,
        height: 'auto',
        width: this.isMobile ? '90%' : '60%',
      }, {
        'before-close': this.handleContinueCheckout
      }
    );
  }

  handleContinueCheckout(input) {
    if (input.params === 'checkout') {
      this.$router.push({
        ...this.$route.params,
        name: Views.ORDER_SUMMARY,
      });
    }
  }

  categoryImagePath(cat: Category) {
    const isMobile = this.isMobile;

    let productFilename = '';
    cat.products.forEach(function(product) {
      const fileName = isMobile
        ? product.mobileImageUrl
        : product.desktopImageUrl;

      if (fileName && fileName !== '' && productFilename === '') {
        productFilename = `${fileName}`;
      }
    });

    return productFilename;
  }

  get activeCategories() {
    const results: Category[] = [];

    if (this.categories) {
      this.categories.forEach((category) => {
        if (category.products && category.products.length > 0) {
          results.push(category);
        }
      });
    }

    return results;
  }

  get products(): Product[] {
    return this.activeCategory?.products?.length
      ? this.activeCategory.products
      : [] as Product[];
  }

  productImagePath(product: Product) {
    const fileName = product?.desktopImageUrl;
    if (!fileName) {
      return null;
    }
    return `${fileName}`;
  }

  get showProducts() {
    return this.activeCategory?.id &&
      this.products?.length &&
      this.$route.name === 'ChooseFoodCategory';
  }

  get activeCategoryName() {
    return this.activeCategory?.name || '';
  }
}
